import classNames from 'classnames';
import Link from 'next/link';
import { ReactNode } from 'react';

import { EditIcon } from '@icons';
import { setupForDark } from '@theme';
import Button from './Button';
import InputErrorMessage from './InputErrorMessage';

interface ComplexFieldEditorLinkProps
  extends Omit<React.HTMLAttributes<HTMLAnchorElement>, 'title'> {
  isEmpty: boolean;
  title: ReactNode;
  summary: string;
  summaryDraft: {
    header: string;
    drafts: string[];
  } | null;
  placeholderIcon: ReactNode;
  error?: string;
  url: string;
  publishFunction: () => void;
  publishLabel: string;
  canPublish: boolean;
}

const ComplexFieldEditorLink = ({
  isEmpty,
  title,
  summary,
  summaryDraft,
  placeholderIcon,
  error,
  url,
  publishFunction,
  canPublish,
  publishLabel = 'Publish',
  className,
  ...props
}: ComplexFieldEditorLinkProps) => {
  const onPublish = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    await publishFunction();
  };

  return (
    <div
      className={classNames(
        'flex flex-col',
        'rounded-sm px-sm pt-xxs pb-md',
        setupForDark('bg-card-tertiary'),
        className
      )}
    >
      <Link href={url} {...props}>
        <div className='flex flex-row items-center justify-between'>
          <div className={classNames('LS2M py-sm mb-xxs uppercase', setupForDark('text-tertiary'))}>
            {title}
          </div>
          <div className='flex flex-row'>
            <EditIcon size={20} className={setupForDark('text-tertiary')} />
          </div>
        </div>
        {isEmpty && (
          <div
            className={classNames(
              'rounded-sm',
              'border-dashed border-[1px]',
              'flex flex-row items-center justify-center',
              setupForDark('border-tertiary text-tertiary'),
              'py-xxl px-md'
            )}
          >
            {placeholderIcon}
          </div>
        )}
        {!isEmpty && (
          <div className={classNames('P2R py-sm', setupForDark('text-tertiary'))}>{summary}</div>
        )}
        {!isEmpty && summaryDraft?.drafts.length > 0 && (
          <div className={classNames('P2R py-sm', setupForDark('text-warning'))}>
            {summaryDraft.header}
            <ul className='list-disc pl-md py-sm'>
              {summaryDraft.drafts.map((draft) => (
                <li key={draft}>{draft}</li>
              ))}
            </ul>
          </div>
        )}
        {error && (
          <div
            className={classNames('mt-xs border-t-[1px] pt-xs', setupForDark('border-secondary'))}
          >
            <InputErrorMessage error={error} />
          </div>
        )}
      </Link>
      {canPublish && (
        <Button onClick={onPublish} type='primary' size='medium' fullWidth={true}>
          {publishLabel}
        </Button>
      )}
    </div>
  );
};

export default ComplexFieldEditorLink;
